/**
 * ZoneSeatLayout 서비스
 * 좌석 배치 계산 및 검증 로직
 */
import type {
  Zone,
  SeatInZone,
  SeatPosition,
  AutoArrangeConfig,
  SeatUpdateData,
} from '../models/ZoneSeatLayout';
import { SEAT_STATUS_CONFIG } from '../../../services/zoneApi';

export class ZoneSeatLayoutService {
  calculateGridPosition(position: SeatPosition, gridSize: number = 40) {
    const row_number = Math.floor(position.y / gridSize) + 1;
    const col_number = Math.floor(position.x / gridSize) + 1;
    const row_label = String.fromCharCode(65 + Math.floor(position.y / gridSize)); // A, B, C...
    const seat_label = String(Math.floor(position.x / gridSize) + 1);

    return {
      row_number,
      col_number,
      row_label,
      seat_label,
    };
  }

  generateSeatNumber(zoneCode: string, counter: number): string {
    return `${zoneCode}-${String(counter).padStart(2, '0')}`;
  }

  calculateAutoArrangeConfig(zone: Zone): AutoArrangeConfig {
    const seatWidth = 40;
    const seatHeight = 40;
    const spacing = 10;
    const margin = 20;

    const seatsPerRow = Math.floor((zone.width - margin * 2 + spacing) / (seatWidth + spacing));
    const seatsPerCol = Math.floor((zone.height - margin * 2 + spacing) / (seatHeight + spacing));
    const totalSeats = seatsPerRow * seatsPerCol;

    return {
      seatWidth,
      seatHeight,
      spacing,
      margin,
      seatsPerRow,
      seatsPerCol,
      totalSeats,
    };
  }

  constrainPositionToZone(position: SeatPosition, zone: Zone, seatSize: number = 40): SeatPosition {
    return {
      x: Math.max(0, Math.min(zone.width - seatSize, position.x)),
      y: Math.max(0, Math.min(zone.height - seatSize, position.y)),
    };
  }

  getSeatStyle(
    seat: SeatInZone,
    isSelected: boolean,
    isDragging: boolean,
    isPreviewMode: boolean
  ): string {
    let baseStyle =
      'absolute w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xs font-bold transition-all duration-200';

    if (isPreviewMode) {
      baseStyle += ' cursor-default';
    } else {
      baseStyle += ' cursor-pointer hover:scale-105';
    }

    if (isDragging) {
      baseStyle += ' opacity-75 z-50 shadow-2xl';
    } else if (isSelected) {
      baseStyle += ' bg-blue-600 border-blue-700 text-white shadow-lg';
    } else if (seat.is_available) {
      baseStyle += ' bg-green-50 border-green-400 text-green-800 hover:bg-green-100';
    } else {
      const config = SEAT_STATUS_CONFIG[seat.status];
      baseStyle += ` ${config.bgColor} border-gray-400 text-gray-600 opacity-60`;
    }

    return baseStyle;
  }

  createNewSeatData(
    zone: Zone,
    position: SeatPosition,
    counter: number
  ): Partial<SeatInZone> {
    const gridPosition = this.calculateGridPosition(position);

    return {
      id: Date.now(), // 임시 ID
      seat_number: this.generateSeatNumber(zone.code, counter),
      seat_type: 'normal',
      position_x: position.x,
      position_y: position.y,
      status: 'available',
      is_available: true,
      hourly_rate: zone.base_hourly_rate,
      daily_rate: zone.base_daily_rate,
      ...gridPosition,
    };
  }

  createDuplicatedSeatData(
    seat: SeatInZone,
    offset: SeatPosition = { x: 50, y: 50 }
  ): Partial<SeatInZone> {
    return {
      ...seat,
      id: Date.now() + Math.random(), // 임시 ID
      seat_number: `${seat.seat_number}-COPY`,
      position_x: seat.position_x + offset.x,
      position_y: seat.position_y + offset.y,
    };
  }

  prepareSeatUpdateData(seat: SeatInZone): SeatUpdateData {
    const gridPosition = this.calculateGridPosition({
      x: seat.position_x,
      y: seat.position_y,
    });

    return {
      seatId: seat.id,
      position_x: seat.position_x,
      position_y: seat.position_y,
      ...gridPosition,
    };
  }
}

export const zoneSeatLayoutService = new ZoneSeatLayoutService();
